import * as React from 'react';
import { connect } from 'react-redux';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import style from './GeotreeInfoPanel.style';
import {
    FMContainer,
    FMTable,
    FMTableBody,
    FMTableRow,
    FMTableCell,
    FMButton,
    FMIcon,
} from 'atoms';
import { NodeInfoData, TreeDisplayStateType, ParamType } from '../../../store/FMTree/FMTreeReducer';
import { getNodeInfoData, getInfoPanelFetching } from '../../../store/FMTree/FMTReeSelectors';
import { replaceInfoPanelOptimizeMode } from '../../../store/FMTree/FMTreeActions';
import { RootState } from '../../../store';
import EmptyPanel from './EmptyPanel';
import { FMColorPicker } from 'organisms';
import { FMLoading } from 'molecules';

interface StateProps {
    data: NodeInfoData;
    infoPanelFetching: boolean;
}

interface DispatchProps {
    replaceInfoPanelOptimizeMode: (displayStateType: string, modeEnabled: boolean) => void;
}

type Props = StateProps & DispatchProps & WithStyles;

const InfoPanel = (props: Props) => {
    const openOptimizePanel = (): void => {
        props.replaceInfoPanelOptimizeMode(
          TreeDisplayStateType.mainGeofencesTree,
          true,
        );
    };

    return (
      <FMContainer className={props.classes.paramsPanel}>
          {
              props.infoPanelFetching && <FMLoading />
          }
          {
              props.data === null ?
                <EmptyPanel />
                :
                <>
                    <div className={props.classes.paramsContainer}>
                        <FMTable>
                            <FMTableBody>
                                {
                                    props.data.params.map(param => (
                                      <FMTableRow key={param.sysName}>
                                          <FMTableCell><Typography>{param.label}:</Typography></FMTableCell>
                                          <FMTableCell>
                                              <div style={{ overflowWrap: 'anywhere' }}>
                                                  {getParamValue(param.type, param.value, param.sysName)}
                                              </div>
                                          </FMTableCell>
                                      </FMTableRow>
                                    ))
                                }
                            </FMTableBody>
                        </FMTable>
                    </div>
                    {
                        props.data.showMinimizeSettings === true &&
                        <div className={props.classes.minimizePanel}>
                            <FMButton
                              variant="outlined"
                              color="primary"
                              onClick={openOptimizePanel}
                            >{fmtools.translate('Optimize')}</FMButton>
                            <Tooltip title={fmtools.translate('OptimizeGeofenceInfoText')}>
                                <FMIcon type="info" color="primary" />
                            </Tooltip>
                        </div>
                    }
                </>
          }
      </FMContainer>
    );
};

const getParamValue = (type: ParamType, value: any, sysName: string) => {
    if (sysName === 'description') {
        let stringValue = value as string;
        let blocksInfo = getValueBlocks(stringValue);
        return (
            <>
                {
                    blocksInfo.map(info => {
                        let text = stringValue.substring(info.startIndex, info.endIndex);
                        if (info.type == ValueType.Text) {
                            return <Typography>{text}</Typography>;
                        }
                        else {
                            return <a href={text} target="_blank">{text}</a>
                        }
                    })
                }
            </>
        );
    }

    switch (type) {
        case ParamType.color: return <FMColorPicker initialColor={value} disabled={true} />;
        case ParamType.icon: return <img src={`../../../img/${value}`} />;
        default: return <Typography>{value.toString()}</Typography>;
    }
};

enum ValueType {
    Text = 1,
    Link = 2,
}
interface ValueInfo {
    type: ValueType;
    startIndex: number;
    endIndex;
}
// Функция возвращает инфу по блокам текста и гиперссылок.
function getValueBlocks(value: string): ValueInfo[] {
    let result: ValueInfo[] = [];

    // Ищем начало гиперссылки. Если её нет, возвращаем только инфу по текстовому блоку.
    let startIndex = value.indexOf('http');
    if (startIndex < 0) {
        return [{
            type: ValueType.Text,
            startIndex: 0,
            endIndex: value.length,
        }];
    }

    // Если гиперссылка есть, но она не сначала, то добавляем блок текста.
    if (startIndex > 0) {
        result.push({
            type: ValueType.Text,
            startIndex: 0,
            endIndex: startIndex,
        });
    }
    while (startIndex >= 0) {
        // Ищем конец гиперссылки, должа быть отделена пробелом или новой строкой.
        // Тут будут проблемы, если сначала будет новая строка, а потом пробел, или
        // будет другой разделитель, но пока оставляю так, потомоу что по идее
        // вообще не должно быть несколько гиперссылок в одном описании.
        let nextTextIndex = value.indexOf(' ', startIndex);
        if (nextTextIndex < 0) {
            nextTextIndex = value.indexOf('\n', startIndex);
        }
        if (nextTextIndex < 0) {
            // Если пробел не найден, считаем всё оставшееся гиперссылкой.
            result.push({
                type: ValueType.Link,
                startIndex,
                endIndex: value.length,
            });
            break;
        }
        else {
            // Если пробел найден, добавляем инфу по блоку гиперрсылки.
            result.push({
                type: ValueType.Link,
                startIndex,
                endIndex: nextTextIndex,
            });
        }

        // Ищем начало следующей гиперссылки
        startIndex = value.indexOf('http', nextTextIndex);
        if (startIndex < 0) {
            // Если не найдена, добавляем инфу по оставшемуся блоку текста.
            result.push({
                type: ValueType.Text,
                startIndex: nextTextIndex,
                endIndex: value.length,
            });
        }
        else {
            // Если найдена, добавляем инфу по блоку текста между гиперссылками.
            result.push({
                type: ValueType.Text,
                startIndex: nextTextIndex,
                endIndex: startIndex,
            });
        }
    }

    return result;
}

const mapStateToProps = (state: RootState): StateProps => ({
    data: getNodeInfoData(state, TreeDisplayStateType.mainGeofencesTree),
    infoPanelFetching: getInfoPanelFetching(state, TreeDisplayStateType.mainGeofencesTree)
});

const mapDispatchToProps = {
    replaceInfoPanelOptimizeMode
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(style)(InfoPanel));
